/**
 * src/lib/lister.ts
 *
 * Implements `cem list` — prints a formatted overview of all modules,
 * middlewares, env vars, and utilities in the current CEM project.
 */

import fs from 'fs';
import path from 'path';
import * as ui from './ui';
import { loadCemConfig } from './configLoader';

function assertCemProject(projectRoot: string): void {
  if (!fs.existsSync(path.join(projectRoot, 'src/app'))) {
    ui.abort(
      'Run this command from the root of a Create Express Modular project.\n' +
      '     Expected to find: src/app/',
    );
  }
}

function readFileSafe(filePath: string): string | null {
  try {
    return fs.readFileSync(filePath, 'utf8');
  } catch {
    return null;
  }
}

function _isModuleWired(projectRoot: string, moduleName: string): boolean {
  const indexPath = path.join(projectRoot, 'src/app/routes/index.ts');
  const content = readFileSafe(indexPath);
  if (!content) return false;
  return content.includes(moduleName);
}

function _isSensitive(key: string): boolean {
  const lower = key.toLowerCase();
  return (
    lower.includes('secret') ||
    lower.includes('password') ||
    lower.includes('token') ||
    lower.includes('key') ||
    lower.includes('private') ||
    lower.includes('api')
  );
}

export function listProject(): void {
  const projectRoot = process.cwd();
  assertCemProject(projectRoot);

  const config = loadCemConfig(projectRoot);

  let projectName = config?.project?.name || path.basename(projectRoot);
  if (!config) {
    try {
      const pkg = JSON.parse(
        fs.readFileSync(path.join(projectRoot, 'package.json'), 'utf8'),
      ) as { name?: string };
      if (pkg.name) projectName = pkg.name;
    } catch {
      /* fallback to dirname */
    }
  }

  const line = ui.gray('─'.repeat(52));
  console.log();
  console.log(`  ${line}`);
  console.log();
  console.log(
    `  ${ui.badge('CEM')}  ${ui.bold(ui.cyan(projectName))}  ${ui.gray('project overview')}`,
  );

  if (config) {
    const authText = config.project.auth ? 'Yes' : 'No';
    console.log(
      `  ${ui.gray('Config')}   DB: ${ui.bold(config.project.db)}  |  Validator: ${ui.bold(config.project.validator)}  |  Auth: ${ui.bold(authText)}`,
    );
  }

  console.log();
  console.log(`  ${line}`);

  // Modules
  const modulesDir = path.join(projectRoot, 'src/app/modules');
  console.log();
  console.log(`  ${ui.bold(ui.cyan('◆  Modules'))}  ${ui.gray('src/app/modules/')}`);
  console.log();

  if (fs.existsSync(modulesDir)) {
    const modules = fs.readdirSync(modulesDir).filter((d) => {
      return fs.statSync(path.join(modulesDir, d)).isDirectory();
    });

    if (modules.length === 0) {
      console.log(`     ${ui.gray('(none — run: cem add module <Name>)')}`);
    } else {
      modules.forEach((mod) => {
        const modDir = path.join(modulesDir, mod);
        const files = fs
          .readdirSync(modDir)
          .filter((f) => f.endsWith('.ts') || f.endsWith('.md'));
        const isWired = _isModuleWired(projectRoot, mod);
        const wired = isWired ? ui.green('● wired') : ui.yellow('○ not wired');

        console.log(`     ${ui.cyan('◈')}  ${ui.white(mod)}  ${wired}`);
        files.forEach((f) => console.log(`        ${ui.gray('·')}  ${ui.gray(f)}`));
      });
    }
  } else {
    console.log(`     ${ui.gray('(modules directory not found)')}`);
  }

  // Middlewares
  const mwDir = path.join(projectRoot, 'src/app/middlewares');
  console.log();
  console.log(
    `  ${ui.bold(ui.cyan('◆  Middlewares'))}  ${ui.gray('src/app/middlewares/')}`,
  );
  console.log();

  if (fs.existsSync(mwDir)) {
    const CORE = new Set([
      'globalErrorHandler.middleware.ts',
      'notFound.middleware.ts',
      'auth.middleware.ts',
      'rateLimiter.middleware.ts',
    ]);
    const allMw = fs.readdirSync(mwDir).filter((f) => f.endsWith('.ts'));
    const coreMw = allMw.filter((f) => CORE.has(f));
    const customMw = allMw.filter((f) => !CORE.has(f));

    if (coreMw.length > 0) {
      coreMw.forEach((f) =>
        console.log(`     ${ui.gray('◇')}  ${ui.gray(f)}  ${ui.gray('[core]')}`),
      );
    }
    if (customMw.length === 0) {
      console.log(
        `     ${ui.gray('(no custom middlewares — run: cem add middleware <name>)')}`,
      );
    } else {
      customMw.forEach((f) => console.log(`     ${ui.cyan('◈')}  ${ui.white(f)}`));
    }
  } else {
    console.log(`     ${ui.gray('(middlewares directory not found)')}`);
  }

  // Environment Variables
  console.log();
  console.log(`  ${ui.bold(ui.cyan('◆  Environment Variables'))}  ${ui.gray('.env')}`);
  console.log();

  const envPath = path.join(projectRoot, '.env');
  const envContent = readFileSafe(envPath);
  if (envContent === null) {
    console.log(`     ${ui.gray('(.env not found)')}`);
  } else {
    const vars = envContent
      .split('\n')
      .filter((l) => l.trim() && !l.trim().startsWith('#') && l.includes('='));

    if (vars.length === 0) {
      console.log(`     ${ui.gray('(no variables defined)')}`);
    } else {
      vars.forEach((v) => {
        const [rawKey, ...rest] = v.split('=');
        const val = rest.join('=').trim();
        const key = rawKey.trim();
        const masked = _isSensitive(key)
          ? ui.gray('<hidden>')
          : ui.gray(val || '<empty>');
        console.log(`     ${ui.cyan('◈')}  ${ui.white(key)}  ${masked}`);
      });
    }
  }

  // Utils
  const utilsDir = path.join(projectRoot, 'src/app/utils');
  console.log();
  console.log(`  ${ui.bold(ui.cyan('◆  Utils'))}  ${ui.gray('src/app/utils/')}`);
  console.log();
  if (fs.existsSync(utilsDir)) {
    fs.readdirSync(utilsDir)
      .filter((f) => f.endsWith('.ts'))
      .forEach((f) => console.log(`     ${ui.gray('·')}  ${ui.gray(f)}`));
  } else {
    console.log(`     ${ui.gray('(utils directory not found)')}`);
  }

  console.log();
  console.log(`  ${line}`);
  console.log();
  console.log(
    `  ${ui.gray('Tip:')}  ${ui.gray('cem remove module <Name> | cem remove middleware <name> | cem remove env <KEY>')}`,
  );
  console.log();
}
